import { z } from 'zod';
import { BuildingIdSchema, LotIdSchema, OperationIdSchema } from '../../ids';
import { ItemIdSchema } from '../../models/items';
import { InventorySchema } from '../../models/inventory';
import { LotStateSchema } from '../../models/lot';
import { StructureDefIdSchema } from '../../models/town';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/**
 * Places a hearth and claims the lot around it, at once (HOME TI-23, RV-24, plan C3b): the hearth
 * is paid from the backpack; the lot (`lotRefusal`, refused as `lot_refused` with its reason) is
 * bought from the power that holds the land (`buy`, its price from the treasury), raised there
 * without leave (`squat`), or taken on land nobody holds (`free`). One home lot per character;
 * a company may hold more (plan E6).
 * Positions in world tiles; the hearth's rotation in quarter turns clockwise from south.
 */
export const placeHearth = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    hearth: z.object({
      tx: z.number().int(),
      ty: z.number().int(),
      rotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
    }),
    lot: z.object({
      x: z.number().int(),
      y: z.number().int(),
      width: z.number().int().positive().max(1_000),
      height: z.number().int().positive().max(1_000),
    }),
    mode: z.enum(['buy', 'squat', 'free']),
    /**
     * Whose lot it is (plan E6): the character's home lot (one, HG-1), or its company's, founded by
     * a rank that claims and paid from the company's treasury (LC-1).
     */
    owner: z.enum(['self', 'company']).default('self'),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    lot: LotStateSchema,
    inventory: InventorySchema,
  }),
});

const RotationSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);

/** Every command on a lot answers with the lot and the (charged) inventory. */
const LotResultSchema = z.object({
  operationId: OperationIdSchema,
  lot: LotStateSchema,
  inventory: InventorySchema,
});

/**
 * Pays a paid lot's canon ahead (HOME technique 8, plan C7): `days` game days from the treasury
 * to the power holding its land, up to the most days ahead; a lot in arrears stops falling. Its
 * owner, from anywhere. Idempotent.
 */
export const payLotRent = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots/:lotId/rent`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({ operationId: OperationIdSchema, days: z.number().int().positive().max(365) }),
  response: LotResultSchema,
});

/**
 * Legalizes a lot raised without leave (RV-30, plan C7): its price plus a fine, to the power
 * holding its land; it becomes paid and stops falling. Its owner, from anywhere. Idempotent.
 */
export const legalizeLot = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots/:lotId/legalize`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: LotResultSchema,
});

/**
 * Claims a small site (HOME technique 14, plan C10): by its fixed hearth, once its custodians are
 * defeated (`site_defended` otherwise), its right paid to the power holding its land; the game
 * lights its hearth and its upkeep runs from the next day. One per character (`conflict`); a
 * fallen one is claimed with what stands on it (SS-1), a held one never (`site_held`). Idempotent.
 */
export const claimSite = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/sites/:siteId/claim`,
  access: 'session',
  params: z.object({ siteId: z.string().regex(/^[a-z][a-z0-9_]*$/) }),
  body: z.object({ operationId: OperationIdSchema }),
  response: LotResultSchema,
});

/**
 * Pitches a temporary camp from a kit in the backpack (HOME technique 13, plan C8): its structure at
 * world tiles `tx`, `ty`, standing by it, off every lot, place, road and way in (`lot_refused` with
 * its reason). One a character (`conflict`); never a home. Idempotent.
 */
export const pitchCamp = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/camps`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    kit: ItemIdSchema,
    tx: z.number().int(),
    ty: z.number().int(),
    rotation: RotationSchema.default(0),
  }),
  response: LotResultSchema,
});

/**
 * Its owner folds its temporary camp back into its kit, standing by it (plan C8, CM-5): the kit
 * and what its chest held come to the backpack, what does not fit lies on the ground. Idempotent.
 */
export const foldCamp = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/camps/:lotId/fold`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Assembles a siege machine at the own siege camp, standing by it (SIEGE H2b, SI-2): its parts
 * (`siege.machines.<machine>.parts`) from the camp's chest first, then the backpack; the machine
 * made into the backpack (a ram, carried by hand: RM-1) or standing on the camp's yard (an engine,
 * H2b2). `validation_failed` (`parts`) when they are not all there. Idempotent.
 */
export const assembleMachine = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/camps/:lotId/machines`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    machine: z.string().min(1).max(64),
    /**
     * Where an engine (a catapult, a ballista: H2b2) stands on the camp's yard, in world tiles: inside
     * its lot, judged like any piece (`location_occupied`), while its yard takes one more
     * (`validation_failed`, `yard_full`). A ram takes none.
     */
    at: z
      .object({ tx: z.number().int(), ty: z.number().int(), rotation: RotationSchema.default(0) })
      .optional(),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    inventory: InventorySchema,
    lot: LotStateSchema,
  }),
});

/**
 * Its owner leaves a lot (LV-1): it falls to ruin (RV-22), its hearth out and nobody's home, its
 * structures standing with their storages open to anyone; nothing paid comes back. From anywhere.
 * Idempotent.
 */
export const abandonLot = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots/:lotId/abandon`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: LotResultSchema,
});

/**
 * Reclaims a lot fallen to ruin (HOME technique 0, RC-1..4), standing by its hearth: its fire lit
 * again (a hearth's cost from the backpack) on its land's terms (`mode`, as `placeHearth`'s); its
 * former owner alone for a while after it fell (`former_owner_first`), then anyone without a home
 * lot. It and all on it, its goods too, become the claimer's. Idempotent.
 */
export const reclaimLot = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots/:lotId/reclaim`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({ operationId: OperationIdSchema, mode: z.enum(['buy', 'squat', 'free']) }),
  response: LotResultSchema,
});

/**
 * Its owner locks a forced storage again (HOME technique 11, plan C6), standing by it: free, what
 * broke was the door, not the lock. Idempotent.
 */
export const relock = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/relock`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ operationId: OperationIdSchema, lot: LotStateSchema }),
});

/**
 * Its people open a gate with a lock (BUILD, audit item 11), standing by it with the lot's `enter`
 * right: it stands open to anyone `interaction.gateOpenMs`, then shuts by itself; opened again
 * while open, it stays open longer. Idempotent.
 */
export const openGate = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/open`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ operationId: OperationIdSchema, lot: LotStateSchema }),
});

/**
 * Feeds a fixed fire (LM-6, the brazier) `count` of a fuel it burns, from the backpack, standing by
 * it with its lot's `enter` right (LX-8 a): paid at once, kept as time of fire up to what it holds
 * (`fire_full`); lit, it burns longer. Refused as `validation_failed` with its `reason` (`no_fire`,
 * `no_fuel`, `fire_full`, `ruined`). Idempotent.
 */
export const feedFire = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/feed`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    item: ItemIdSchema,
    count: z.number().int().positive().max(1_000),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    lot: LotStateSchema,
    inventory: InventorySchema,
  }),
});

/**
 * Lights a fixed fire with the time of fire it keeps (`no_fuel` without any), or puts it out keeping
 * what is left (LM-6, LX-8 a), standing by it with its lot's `enter` right. Refused as
 * `validation_failed` with its `reason` (`fire_lit`, `fire_out`, `no_fuel`, `no_fire`, `ruined`).
 * Idempotent.
 */
export const lightFire = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/light`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, lit: z.boolean() }),
  response: z.object({
    operationId: OperationIdSchema,
    lot: LotStateSchema,
    inventory: InventorySchema,
  }),
});

/**
 * Repairs a broken piece of a lot (SIEGE H2a, R-59), standing by it, with its `build` right: with
 * `siege.repair.costShare` of its cost it stands whole again but patched (`patch`); with its whole
 * cost, rebuilt, unpatched (`rebuild`). Paid from the backpack; a hearth repaired heals its wound.
 * Any time, a siege too. Idempotent.
 */
export const repairStructure = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/repair`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, mode: z.enum(['patch', 'rebuild']) }),
  response: LotResultSchema,
});

/**
 * Fills a turret's magazine by hand standing by it (SIEGE H2e): up to `count` of its ammunition
 * from the backpack, as much as it still takes. The lot's `store` right. Idempotent.
 */
export const loadTurret = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/buildings/:buildingId/turret`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({ operationId: OperationIdSchema, count: z.number().int().positive().max(1_000) }),
  response: LotResultSchema,
});

/**
 * Builds on the own lot (C5): a structure whose definition stands on lots (`sites`), in tiles of
 * the lot, turned. A project starts as one (BUILD); any other is paid at once from the backpack.
 * The lot's hearth must be lit.
 */
export const placeOnLot = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/lots/:lotId/structures`,
  access: 'session',
  params: z.object({ lotId: LotIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    kind: StructureDefIdSchema,
    tx: z.number().int().nonnegative().max(1_000),
    ty: z.number().int().nonnegative().max(1_000),
    rotation: RotationSchema,
  }),
  response: LotResultSchema,
});
